/* eslint-disable react/no-danger -- HTML is sanitized with DOMPurify before injection */
import { sanitizeStoredHtml } from 'common/utils/html'
import type { HTMLAttributes } from 'react'
import styled from 'styled-components'

const Root = styled.div<{ $inline?: boolean }>`
  display: ${({ $inline }) => ($inline ? 'inline' : 'block')};

  p {
    margin: ${({ $inline }) => ($inline ? '0' : '0 0 0.35em')};
    display: ${({ $inline }) => ($inline ? 'inline' : 'block')};
  }
  p:last-child {
    margin-bottom: 0;
  }
  h1 {
    font-size: 1.25em;
  }
  h2 {
    font-size: 1.15em;
  }
  h3 {
    font-size: 1.05em;
  }
  h1,
  h2,
  h3 {
    margin: ${({ $inline }) => ($inline ? '0' : '0 0 0.35em')};
    display: ${({ $inline }) => ($inline ? 'inline' : 'block')};
    font-weight: 600;
  }
  ul,
  ol {
    margin: ${({ $inline }) => ($inline ? '0' : '0 0 0.35em 1.25em')};
    padding: ${({ $inline }) => ($inline ? '0 0 0 1.25em' : '0')};
    display: ${({ $inline }) => ($inline ? 'inline' : 'block')};
  }
  li {
    margin: ${({ $inline }) => ($inline ? '0' : '0.15em 0')};
    display: ${({ $inline }) => ($inline ? 'inline' : 'list-item')};
  }
  a {
    color: ${({ theme }) => theme.color.status.primary};
  }
`

export type ViewStoredHtmlProps = Pick<
  HTMLAttributes<HTMLDivElement>,
  'className' | 'dangerouslySetInnerHTML'
> & {
  inline?: boolean
}

/**
 * Renders RichEditor HTML from our API or user-entered fields. Content is sanitized
 * with DOMPurify before `dangerouslySetInnerHTML` (same pattern as goals copy, search
 * highlights, and user-generated field labels).
 */
export function ViewStoredHtml({
  className,
  dangerouslySetInnerHTML,
  inline = false
}: ViewStoredHtmlProps) {
  const sanitized = sanitizeStoredHtml(dangerouslySetInnerHTML?.__html)
  // Ensure all links open in a new tab. The TipTap editor always serialises
  // target="_blank" for new content, but legacy stored HTML may not have it.
  const __html = sanitized.replace(
    /<a\b((?:[^>"']|"[^"]*"|'[^']*')*?)>/gi,
    (_match, attrs) => {
      const cleaned = attrs
        .replace(/\s+target="[^"]*"/gi, '')
        .replace(/\s+rel="[^"]*"/gi, '')
      return `<a${cleaned} target="_blank" rel="noopener noreferrer">`
    }
  )

  return (
    // biome-ignore lint/security/noDangerouslySetInnerHtml: string is sanitized with DOMPurify immediately above
    <Root
      className={className}
      $inline={inline}
      dangerouslySetInnerHTML={{ __html }}
    />
  )
}
